export type BatchPlanningPreviewControllerOptions<Payload, Response> = {
  debounceMs?: number
  isReady: () => boolean
  buildPayload: () => Payload | null
  validatePayload: (payload: Payload) => string | null
  loadPreview: (payload: Payload, signal: AbortSignal) => Promise<Response>
  getCurrentResponse: () => Response | null
  setResponse: (response: Response | null) => void
  setLoading: (loading: boolean) => void
  setPending: (pending: boolean) => void
  setError: (message: string | null) => void
  onApplied?: (payload: Payload, response: Response) => void
  onUnexpectedError?: (error: unknown) => void
  errorMessage?: string
}

export type BatchPlanningPreviewRefreshOptions = {
  force?: boolean
}

export type BatchPlanningPreviewController = {
  refresh: (options?: BatchPlanningPreviewRefreshOptions) => Promise<boolean>
  queue: (delay?: number, options?: BatchPlanningPreviewRefreshOptions) => void
  cancel: () => void
  destroy: () => void
}

export const createBatchPlanningPreviewController = <Payload, Response>({
  debounceMs = 350,
  isReady,
  buildPayload,
  validatePayload,
  loadPreview,
  getCurrentResponse,
  setResponse,
  setLoading,
  setPending,
  setError,
  onApplied,
  onUnexpectedError,
  errorMessage = "Ergebnisvorschau konnte gerade nicht berechnet werden.",
}: BatchPlanningPreviewControllerOptions<Payload, Response>): BatchPlanningPreviewController => {
  let timer: ReturnType<typeof setTimeout> | null = null
  let requestToken = 0
  let lastSignature = ""
  let queuedForceRefresh = false
  let activeRequest: {
    controller: AbortController
    signature: string
    promise: Promise<boolean>
  } | null = null

  const clearQueue = () => {
    if (timer) clearTimeout(timer)

    timer = null
    queuedForceRefresh = false
    setPending(false)
  }

  const invalidateActiveRequest = () => {
    requestToken += 1
    activeRequest?.controller.abort()
    activeRequest = null
    setLoading(false)
  }

  const reset = () => {
    setResponse(null)
    setError(null)
    setLoading(false)
    lastSignature = ""
  }

  const refresh = async (options: BatchPlanningPreviewRefreshOptions = {}) => {
    const force = Boolean(options.force) || queuedForceRefresh
    clearQueue()

    if (!isReady()) {
      invalidateActiveRequest()
      reset()

      return false
    }

    const payload = buildPayload()

    if (!payload) {
      invalidateActiveRequest()
      reset()

      return false
    }

    const validationMessage = validatePayload(payload)

    if (validationMessage) {
      invalidateActiveRequest()
      reset()
      setError(validationMessage)

      return false
    }

    const signature = JSON.stringify(payload)

    // Force bypasses completed results, not identical work already in flight.
    if (activeRequest?.signature === signature) return activeRequest.promise

    invalidateActiveRequest()

    if (!force && signature === lastSignature && getCurrentResponse()) return true

    const token = requestToken
    reset()
    setLoading(true)
    const controller = new AbortController()
    const request = { controller, signature, promise: Promise.resolve(false) }
    activeRequest = request
    request.promise = (async () => {
      try {
        const response = await loadPreview(payload, controller.signal)

        if (token !== requestToken || controller.signal.aborted) return false

        lastSignature = signature
        setResponse(response)
        onApplied?.(payload, response)

        return true
      } catch (error) {
        if (token !== requestToken || controller.signal.aborted) return false

        if (
          typeof error === "object" &&
          error !== null &&
          "name" in error &&
          error.name === "AbortError"
        ) {
          return false
        }

        lastSignature = ""
        setResponse(null)
        setError(errorMessage)
        onUnexpectedError?.(error)

        return false
      } finally {
        if (activeRequest === request) activeRequest = null

        if (token === requestToken) setLoading(false)
      }
    })()

    return request.promise
  }

  const queue = (delay = debounceMs, options: BatchPlanningPreviewRefreshOptions = {}) => {
    const force = queuedForceRefresh || Boolean(options.force)
    clearQueue()
    const payload = isReady() ? buildPayload() : null
    const validationMessage = payload ? validatePayload(payload) : null

    if (!payload || validationMessage) {
      invalidateActiveRequest()
      reset()
      setError(validationMessage)

      return
    }

    const signature = JSON.stringify(payload)

    if (activeRequest?.signature === signature) return

    // Changed inputs invalidate both active and completed work before the debounce expires.
    invalidateActiveRequest()

    if (signature !== lastSignature) reset()

    queuedForceRefresh = force
    setPending(true)
    timer = setTimeout(() => {
      timer = null
      const force = queuedForceRefresh
      queuedForceRefresh = false
      void refresh({ force })
    }, delay)
  }

  const cancel = () => {
    clearQueue()
    invalidateActiveRequest()
  }

  return { refresh, queue, cancel, destroy: cancel }
}
